
import { Component, Vue, Watch } from 'vue-property-decorator';
import { ServiceQueueItemFilterTypeEnum, ServiceQueueActionEnum, ServiceQueueItemModel } from '@/libs/Api';
import NDialog from '@/libs/common/components/NDialog.vue';
import { serviceQueueItemsStore } from '@/libs/service-queue-items/+state/store';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import Pagination from '@/libs/common/components/Pagination.vue';
import moment from 'moment';
import { tenantUsersStore } from '@/libs/tenants/+state/store';
import ApiService from '@/libs/core/api-service';
import { AggregationEnum, QueryOperator, JunctionOperator, PlanItemStateEnum, ImplementationStateEnum } from '@/libs/Api';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { PermissionTypeEnum } from '@/libs/Api';
import Page from '@/Page.vue';

interface AgendaTransferFilters {
    filterTypes: string[]; // změna na pole
    fromDate: string | null;
    toDate: string | null;
    fromValue: string | { id: string } | null;
}

@Component({
    components: { NDialog, Pagination },
})
export default class AgendaTransfer extends Page {
    dialog = false;
    filters: AgendaTransferFilters = {
        filterTypes: [], // změna na pole
        fromDate: null,
        toDate: null,
        fromValue: null,
    };
    newValue: string | null = null;
    valid = false;
    error: string | null = null;

    agendaPermissionWarning = '';

    // Počítání záznamů podle filtrů v dialogu
    countLoading = false;
    count: number | null = null; // celkový součet (pro zpětnou kompatibilitu)
    countByType: Record<string, number> = {}; // počty podle typu
    countTimeout: any = null;

    // Tabulka
    pageIndex = 1;
    pageSize = 50;

    mounted() {
        this.loadTable();
    }

    @Watch('pageIndex')
    @Watch('pageSize')
    pageChanged() {
        this.loadTable();
    }

    loadTable() {
        const filters = [{ field: 'action', op: 'eq', comparand: ServiceQueueActionEnum.AgendaMove }];
        const payload = createCrudQueryPayload(
            { field: 'createdAt', index: 1, order: 'desc' },
            filters,
            { pageIndex: this.pageIndex, pageSize: this.pageSize },
            false,
            'AgendaTransfer'
        );
        serviceQueueItemsStore.dispatch(CrudAction.GetAll, payload);
    }

    get loading() {
        return serviceQueueItemsStore.useGetter(CrudGetter.Pending);
    }

    get items() {
        return serviceQueueItemsStore.useGetter(CrudGetter.Data, 'AgendaTransfer') || [];
    }

    get totalRows() {
        return (serviceQueueItemsStore.useGetter(CrudGetter.DataStats, 'AgendaTransfer') as any)?.rowCount || 0;
    }

    get tableHeaders() {
        return [
            { text: this.$t('agendaTransfer.tableCreatedAt'), value: 'createdAt', align: 'left', sortable: false },
            { text: this.$t('agendaTransfer.tableType'), value: 'type', align: 'left', sortable: false },
            { text: this.$t('agendaTransfer.tableFrom'), value: 'from', align: 'left', sortable: false },
            { text: this.$t('agendaTransfer.tableTo'), value: 'to', align: 'left', sortable: false },
            { text: this.$t('agendaTransfer.tableFromDate'), value: 'fromDate', align: 'left', sortable: false },
            { text: this.$t('agendaTransfer.tableToDate'), value: 'toDate', align: 'left', sortable: false },
            { text: this.$t('agendaTransfer.tableState'), value: 'state', align: 'left', sortable: false },
            { text: '', value: 'error', align: 'left', sortable: false },
        ];
    }

    filterTypeText(type: ServiceQueueItemFilterTypeEnum | undefined): string {
        const map: Record<string, string> = {
            [ServiceQueueItemFilterTypeEnum.Plan]: String(this.$t('agendaTransfer.plan')),
            [ServiceQueueItemFilterTypeEnum.Findings]: String(this.$t('agendaTransfer.findings')),
            [ServiceQueueItemFilterTypeEnum.Measures]: String(this.$t('agendaTransfer.measures')),
            [ServiceQueueItemFilterTypeEnum.Verification]: String(this.$t('agendaTransfer.verification')),
            [ServiceQueueItemFilterTypeEnum.Review]: String(this.$t('agendaTransfer.review')),
        };
        return type && map[type] ? map[type] : String(type || '');
    }

    formatDateTime(value: string | null | undefined): string {
        if (!value) return '';
        return moment(value).format('L LT');
    }

    get filterTypeOptions() {
        return [
            { value: ServiceQueueItemFilterTypeEnum.Plan, text: this.$t('agendaTransfer.plan') },
            { value: ServiceQueueItemFilterTypeEnum.Findings, text: this.$t('agendaTransfer.findings') },
            { value: ServiceQueueItemFilterTypeEnum.Measures, text: this.$t('agendaTransfer.measures') },
            { value: ServiceQueueItemFilterTypeEnum.Verification, text: this.$t('agendaTransfer.verification') },
            { value: ServiceQueueItemFilterTypeEnum.Review, text: this.$t('agendaTransfer.review') },
        ];
    }

    closeDialog() {
        this.dialog = false;
        this.resetForm();
        this.count = null;
        this.countLoading = false;
    }

    resetForm() {
        this.filters = {
            filterTypes: [], // změna na pole
            fromDate: null,
            toDate: null,
            fromValue: null,
        };
        this.newValue = null;
        this.valid = false;
        this.error = null;
        this.count = null;
        this.countLoading = false;
    }

    // Watchery na filtry v dialogu
    @Watch('filters', { deep: true })
    @Watch('newValue')
    onFiltersChanged() {
        this.fetchCount();
    }

    // Mapování typů agend na potřebná oprávnění
    agendaTypeToPermission(agendaType: string): PermissionTypeEnum[] {
        switch (agendaType) {
            case 'Plan':
                return [PermissionTypeEnum.Plan];
            case 'Findings':
                return [PermissionTypeEnum.Findings];
            case 'Measures':
                return [PermissionTypeEnum.Measures, PermissionTypeEnum.MeasuresAllUsers];
            case 'Verification':
                return [PermissionTypeEnum.MeasureVerification];
            case 'Review':
                return [PermissionTypeEnum.PlanItemReview];
            default:
                return [];
        }
    }

    // Kontrola oprávnění pro uživatele na převod agendy
    checkAgendaPermissionsForUser(userId: string, agendaTypes: string[]): string[] {
        // Najdi uživatele v tenantUsersStore
        const users = tenantUsersStore.useGetter(CrudGetter.Data) || [];
        const user = users.find((u: any) => u.id === userId);
        if (!user || !user.userRoles) return agendaTypes; // Pokud neznám uživatele, vrať vše jako neprošlé
        // Získej všechna oprávnění z userRoles
        const userPermissions: string[] = [];
        user.userRoles.forEach((ur: any) => {
            if (ur.role && ur.role.permissions) {
                ur.role.permissions.forEach((perm: any) => {
                    if (perm.permissionType && !userPermissions.includes(perm.permissionType)) {
                        userPermissions.push(perm.permissionType);
                    }
                });
            }
        });
        // Pro každou agendu ověř, zda má uživatel aspoň jedno z požadovaných oprávnění
        return agendaTypes.filter((type) => {
            const perms = this.agendaTypeToPermission(type);
            return !perms.some((p) => userPermissions.includes(p));
        });
    }

    async fetchCount() {
        // Zkontroluj, zda jsou všechna pole vyplněná a validní
        if (
            !this.filters ||
            !this.filters.filterTypes ||
            !Array.isArray(this.filters.filterTypes) ||
            this.filters.filterTypes.length === 0 ||
            !this.filters.fromDate ||
            !this.filters.toDate ||
            !this.filters.fromValue ||
            !this.newValue
        ) {
            this.count = null;
            this.countByType = {};
            this.countLoading = false;
            return;
        }
        // Debounce, aby se nevolalo při každém stisku
        if (this.countTimeout) clearTimeout(this.countTimeout);
        this.countLoading = true;
        this.count = null;
        this.countByType = {};
        const filters = this.filters;
        this.countTimeout = setTimeout(async () => {
            try {
                const typeCounts = await Promise.all(
                    filters.filterTypes.map(async (type) => {
                        if (type === 'Plan') {
                            const predicates = [
                                {
                                    field: 'id',
                                    op: QueryOperator.Wrap,
                                    comparand: [
                                        {
                                            field: 'tenantUserId',
                                            op: QueryOperator.Eq,
                                            comparand:
                                                typeof filters.fromValue === 'object' &&
                                                filters.fromValue !== null &&
                                                'id' in filters.fromValue
                                                    ? (filters.fromValue as any).id
                                                    : filters.fromValue,
                                            junction: JunctionOperator.Or,
                                        },
                                        {
                                            field: 'planItemAuditTenantUsers.tenantUserId',
                                            op: QueryOperator.Eq,
                                            comparand:
                                                typeof filters.fromValue === 'object' &&
                                                filters.fromValue !== null &&
                                                'id' in filters.fromValue
                                                    ? (filters.fromValue as any).id
                                                    : filters.fromValue,
                                            junction: JunctionOperator.Or,
                                        },
                                    ],
                                },
                                { field: 'time', op: QueryOperator.Gte, comparand: filters.fromDate },
                                { field: 'time', op: QueryOperator.Lte, comparand: filters.toDate },
                                { field: 'isAnswered', op: QueryOperator.Eq, comparand: false },
                            ];
                            const aggregation = {
                                predicates,
                                aggregation: AggregationEnum.Count,
                                aggregationProperty: 'id',
                            };
                            const response = await ApiService.api.aggregationPlanItem(aggregation);
                            let result = 0;
                            if (response && response.data && response.data.returnValue) {
                                const r = response.data.returnValue;
                                result = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                            }
                            return { type, count: result };
                        } else if (type === 'Findings') {
                            const predicates = [
                                {
                                    field: 'findingManagers.tenantUserId',
                                    op: QueryOperator.Eq,
                                    comparand:
                                        typeof filters.fromValue === 'object' &&
                                        filters.fromValue !== null &&
                                        'id' in filters.fromValue
                                            ? (filters.fromValue as any).id
                                            : filters.fromValue,
                                },
                                {
                                    field: 'response.planItem.planItemState',
                                    op: QueryOperator.Eq,
                                    comparand: PlanItemStateEnum.NeedsToCreateMeasures,
                                },
                            ];
                            const aggregation = {
                                predicates,
                                aggregation: AggregationEnum.Count,
                                aggregationProperty: 'id',
                            };
                            const response = await ApiService.api.aggregationFinding(aggregation);
                            let result = 0;
                            if (response && response.data && response.data.returnValue) {
                                const r = response.data.returnValue;
                                result = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                            }
                            return { type, count: result };
                        } else if (type === 'Measures') {
                            const predicates = [
                                {
                                    field: 'id',
                                    op: QueryOperator.Wrap,
                                    comparand: [
                                        {
                                            field: 'implementerUserId',
                                            op: QueryOperator.Eq,
                                            comparand:
                                                typeof filters.fromValue === 'object' &&
                                                filters.fromValue !== null &&
                                                'id' in filters.fromValue
                                                    ? (filters.fromValue as any).id
                                                    : filters.fromValue,
                                            junction: JunctionOperator.Or,
                                        },
                                        {
                                            field: 'measureImplementationUsers.tenantUserId',
                                            op: QueryOperator.Eq,
                                            comparand:
                                                typeof filters.fromValue === 'object' &&
                                                filters.fromValue !== null &&
                                                'id' in filters.fromValue
                                                    ? (filters.fromValue as any).id
                                                    : filters.fromValue,
                                            junction: JunctionOperator.Or,
                                        },
                                    ],
                                },
                                { field: 'implementUntil', op: QueryOperator.Gte, comparand: filters.fromDate },
                                { field: 'implementUntil', op: QueryOperator.Lte, comparand: filters.toDate },
                                {
                                    field: 'implementationState',
                                    op: QueryOperator.In,
                                    comparand: [ImplementationStateEnum.New, ImplementationStateEnum.InProgress],
                                },
                            ];
                            const aggregation = {
                                predicates,
                                aggregation: AggregationEnum.Count,
                                aggregationProperty: 'id',
                            };
                            const response = await ApiService.api.aggregationMeasure(aggregation);
                            let result = 0;
                            if (response && response.data && response.data.returnValue) {
                                const r = response.data.returnValue;
                                result = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                            }
                            return { type, count: result };
                        } else if (type === 'Verification') {
                            const predicates = [
                                {
                                    field: 'id',
                                    op: QueryOperator.Wrap,
                                    comparand: [
                                        {
                                            field: 'verificationUserId',
                                            op: QueryOperator.Eq,
                                            comparand:
                                                typeof filters.fromValue === 'object' &&
                                                filters.fromValue !== null &&
                                                'id' in filters.fromValue
                                                    ? (filters.fromValue as any).id
                                                    : filters.fromValue,
                                            junction: JunctionOperator.Or,
                                        },
                                        {
                                            field: 'measureVerificationUsers.tenantUserId',
                                            op: QueryOperator.Eq,
                                            comparand:
                                                typeof filters.fromValue === 'object' &&
                                                filters.fromValue !== null &&
                                                'id' in filters.fromValue
                                                    ? (filters.fromValue as any).id
                                                    : filters.fromValue,
                                            junction: JunctionOperator.Or,
                                        },
                                    ],
                                },
                                {
                                    field: 'implementationState',
                                    op: QueryOperator.In,
                                    comparand: [ImplementationStateEnum.WaitingForVerification],
                                },
                            ];
                            const aggregation = {
                                predicates,
                                aggregation: AggregationEnum.Count,
                                aggregationProperty: 'id',
                            };
                            const response = await ApiService.api.aggregationMeasure(aggregation);
                            let result = 0;
                            if (response && response.data && response.data.returnValue) {
                                const r = response.data.returnValue;
                                result = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                            }
                            return { type, count: result };
                        } else if (type === 'Review') {
                            const predicates = [
                                {
                                    field: 'requestReviewById',
                                    op: QueryOperator.Eq,
                                    comparand:
                                        typeof filters.fromValue === 'object' &&
                                        filters.fromValue !== null &&
                                        'id' in filters.fromValue
                                            ? (filters.fromValue as any).id
                                            : filters.fromValue,
                                },
                                {
                                    field: 'planItemState',
                                    op: QueryOperator.Eq,
                                    comparand: PlanItemStateEnum.WaitingForApproval,
                                },
                            ];
                            const aggregation = {
                                predicates,
                                aggregation: AggregationEnum.Count,
                                aggregationProperty: 'id',
                            };
                            const response = await ApiService.api.aggregationPlanItem(aggregation);
                            let result = 0;
                            if (response && response.data && response.data.returnValue) {
                                const r = response.data.returnValue;
                                result = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                            }
                            return { type, count: result };
                        } else {
                            return { type, count: 0 };
                        }
                    })
                );
                this.countByType = {};
                typeCounts.forEach(({ type, count }) => {
                    this.countByType[type] = count;
                });
                this.count = typeCounts.reduce((acc, val) => acc + val.count, 0);
            } catch (e) {
                this.count = 0;
                this.countByType = {};
            }
            this.countLoading = false;
        }, 500);

        // Kontrola oprávnění pro nového uživatele
        this.agendaPermissionWarning = '';
        if (this.newValue) {
            const newValueId = typeof this.newValue === 'object' ? (this.newValue as any).id : this.newValue;
            const failed = this.checkAgendaPermissionsForUser(newValueId, this.filters.filterTypes);
            if (failed.length > 0) {
                this.agendaPermissionWarning = String(
                    this.$t('agendaTransfer.permissionWarning', {
                        user: this.getUserDisplayName(newValueId),
                        agendas: failed.map((type) => this.getFilterTypeTextForTableRow({ filterTypes: type })).join(', '),
                    })
                );
            }
        }
    }

    async onSubmit() {
        (this.$refs.form as any).validate();
        if (
            !this.filters ||
            !this.filters.filterTypes ||
            !Array.isArray(this.filters.filterTypes) ||
            this.filters.filterTypes.length === 0 ||
            !this.filters.fromDate ||
            !this.filters.toDate ||
            !this.filters.fromValue ||
            !this.newValue
        ) {
            this.error = 'required';
            return;
        }
        this.error = null;
        // Najdi displayName podle ID z tenantUsersStore
        let fromValueId =
            typeof this.filters.fromValue === 'object' ? (this.filters.fromValue as any).id : this.filters.fromValue;
        let newValueId = typeof this.newValue === 'object' ? (this.newValue as any).id : this.newValue;
        const users = tenantUsersStore.useGetter(CrudGetter.Data) || [];
        const fromUser = users.find((u: any) => u.id === fromValueId);
        const toUser = users.find((u: any) => u.id === newValueId);
        const fromValueName = fromUser?.user?.displayName || '';
        const newValueName = toUser?.user?.displayName || '';
        try {
            const item = {
                action: ServiceQueueActionEnum.AgendaMove,
                filters: {
                    filterTypes: this.filters.filterTypes, // nyní pole
                    fromDate: this.filters.fromDate,
                    toDate: this.filters.toDate ? moment(this.filters.toDate).endOf('day').toISOString() : null,
                    fromValue: fromValueId,
                    fromValueName,
                },
                newValue: newValueId,
                newValueName,
            };
            await serviceQueueItemsStore.dispatch(CrudAction.Create, { item });
            this.closeDialog();
            this.loadTable();
        } catch (e) {
            this.error = 'server';
        }
    }

    getFilterTypeTextForTableRow(filters: any): string {
        if (!filters || !filters.filterTypes) return '';
        if (Array.isArray(filters.filterTypes)) {
            return filters.filterTypes.map((t: ServiceQueueItemFilterTypeEnum) => this.filterTypeText(t)).join(', ');
        }
        return this.filterTypeText(filters.filterTypes);
    }

    getUserDisplayName(userId: string): string {
        const users = tenantUsersStore.useGetter(CrudGetter.Data) || [];
        const user = users.find((u: any) => u.id === userId);
        return user?.user?.displayName || userId;
    }
}
